<template>
  <div class="mb-4 d-flex flex-row-reverse">
    <SoCopyToClipboard :element-to-copy="element">
      <template #button="{ copyToClipboard }">
        <SoButton
          title="Copy details"
          prepend-icon="copy sm"
          type="table-action"
          :disabled="isTableLoading"
          @click="copyToClipboard()"
        />
      </template>
    </SoCopyToClipboard>
  </div>
  <div ref="suggested-payment-summary">
    <div class="industry-mp-estimates-container">
      <MeterpointUsageSummary
        v-for="meterpoint in meterpoints"
        :key="meterpoint.id"
        :meterpoint="meterpoint"
        :is-estimated="useUsageAmendedMeterpoints"
        :loading="current.meterpoints.isFetching"
      />
    </div>

    <SoSummaryTable
      :items="paymentCalcSummaryItems"
      :summary="paymentCalcSummaryTotal"
      :loading="isTableLoading"
      :aria-headers="['Payment/Balance Type', 'Amount']"
      title="Payment Calculation Summary"
      class="mt-5"
      collapsable
      open
    >
      <template #footer>
        <div class="d-flex align-center gap-1">
          <SoCheckbox
            v-model="showAccountBalance"
            class="included-payments-checkbox"
            :label="`Include Account Balance (${accountBalance})`"
            :disabled="hasActiveDebtPaymentPlan"
            data-dd-action-name="includeAccountBalanceCheckbox"
          />
          <SoTooltip
            v-if="hasActiveDebtPaymentPlan"
            text="
              This customer has an agreed payment plan which should take care of their existing
              debit balance, allowing you to focus the DD review only on their usage. If their
              debit balance is still increasing, you may need to review the payment plan and
              agree a new one or cancel the existing payment plan and include their account
              balance into the DD review calculations."
            data-testid="tooltip-debt-payment-plan-active"
          >
            <SoIcon icon="help-circle" size="small" />
          </SoTooltip>
        </div>

        <SoCheckbox
          v-model="includeUnbilledReadings"
          class="included-payments-checkbox"
          label="Include Unbilled Readings"
          :disabled="!showAccountBalance"
          data-dd-action-name="includeUnbilledReadsCheckbox"
        />
      </template>
    </SoSummaryTable>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { useReviewPaymentStore } from "@/stores/reviewPayment"
  import { formatMoney } from "@/helpers/moneyHelpers"
  import { getSuggestedPaymentBreakdown } from "@/services-handlers/getSuggestedPayment"
  import SoSummaryTable from "@/components/SoSummaryTable.vue"
  import MeterpointUsageSummary from "./MeterpointUsageSummary.vue"
  import SoCopyToClipboard, {
    CopyToClipboardElement,
  } from "@/components/SoCopyToClipboard.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import {
    SoSummaryTableItem,
    SoSummaryTableValueType,
  } from "@/types/components/SoSummaryTable"

  export default defineComponent({
    components: {
      SoSummaryTable,
      MeterpointUsageSummary,
      SoCopyToClipboard,
      SoCheckbox,
      SoButton,
      SoIcon,
      SoTooltip,
    },
    props: {
      useUsageAmendedMeterpoints: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        current: useCurrentAccountStore(),
        reviewPayment: useReviewPaymentStore(),
        activeDebtPaymentPlan: useCurrentAccountStore().activeDebtPaymentPlan,

        showPaymentCalcSummary: true,
        showAccountBalance:
          useCurrentAccountStore().suggestedPaymentParams.includeBalance,
        includeUnbilledReadings: false,

        element: null as CopyToClipboardElement,
        formatMoney,
      }
    },
    computed: {
      hasActiveDebtPaymentPlan() {
        return !!this.activeDebtPaymentPlan.data
      },
      meterpoints() {
        return this.useUsageAmendedMeterpoints
          ? this.reviewPayment.meterpointsWithNewUsageEstimations
          : this.current.meterpoints.data
      },
      accountBalance() {
        const balance = -this.current.account.data?.balance
        return (
          formatMoney({ amount: balance }) +
          `${balance > 0 ? " Credit" : " Debit"}`
        )
      },
      isTableLoading() {
        return (
          this.current.account.isFetching ||
          this.current.currentPaymentSchedule.isFetching ||
          getSuggestedPaymentBreakdown(this.current.accountNumber, false, false)
            .isFetching
        )
      },
      paymentCalcSummaryItems(): SoSummaryTableItem[] {
        return [
          {
            type: SoSummaryTableValueType.BALANCE,
            title: "Account Balance",
            value: this.current.account.data?.balance,
          },
          {
            type: SoSummaryTableValueType.MONEY,
            title: "Unbilled Readings Usage",
            value: this.unbilledReadingsUsage,
          },
          {
            type: SoSummaryTableValueType.BALANCE,
            title: "Balance Including Last Readings",
            value: this.balanceIncludingUnbilled,
          },
          {
            type: SoSummaryTableValueType.MONEY,
            title: "Current Monthly Payment",
            value: this.current.currentPaymentSchedule.data?.amount,
          },
          {
            type: SoSummaryTableValueType.MONEY,
            title: "Suggested Monthly Payment",
            value: getSuggestedPaymentBreakdown(
              this.current.accountNumber,
              false,
              false,
            )?.data?.recommendedMonthlyPayment,
          },
        ]
      },
      paymentCalcSummaryTotal(): SoSummaryTableItem {
        const yearlySuggestedPayment = this.suggestedPayment * 12
        let totalValue = ""
        totalValue +=
          formatMoney({ amount: this.suggestedPayment }) + "/month\n"
        totalValue +=
          formatMoney({ amount: yearlySuggestedPayment }) + "/yearly"

        return {
          type: SoSummaryTableValueType.TEXT,
          title: "Total cost",
          value: totalValue,
        }
      },

      suggestedPayment() {
        return (
          getSuggestedPaymentBreakdown(
            this.current.accountNumber,
            this.current.suggestedPaymentParams.includeBalance,
            this.includeUnbilledReadings,
          ).data?.recommendedMonthlyPayment || 0
        )
      },
      unbilledReadingsUsage() {
        return (
          getSuggestedPaymentBreakdown(
            this.current.accountNumber,
            this.current.suggestedPaymentParams.includeBalance,
            this.includeUnbilledReadings,
          ).data?.unbilledReadingsUsage || 0
        )
      },
      balanceIncludingUnbilled() {
        return this.current.account.data?.balance + this.unbilledReadingsUsage
      },
    },
    watch: {
      showAccountBalance(newVal: boolean) {
        this.current.suggestedPaymentParams.includeBalance = newVal

        if (!newVal) {
          this.includeUnbilledReadings = false
        }
      },
      hasActiveDebtPaymentPlan: {
        immediate: true,
        handler(newVal: boolean) {
          if (newVal) {
            this.showAccountBalance = false
            this.current.suggestedPaymentParams.includeBalance = false
          }
        },
      },
    },
    mounted() {
      this.element = this.$refs["suggested-payment-summary"] as HTMLElement
    },
    methods: {
      toggleSummaryCalculation() {
        this.showPaymentCalcSummary = !this.showPaymentCalcSummary
      },
    },
  })
</script>

<style scoped>
  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0 12px;
  }
  th {
    padding-bottom: 2px;
    border-bottom: 1px solid rgb(var(--v-theme-border));
  }
  tr th:nth-child(1) {
    text-align: left;
    font-weight: 300;
  }
  tr th:nth-child(2) {
    text-align: right;
    font-weight: 300;
  }
  .industry-mp-estimates-container {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }
  .summary-calc {
    cursor: pointer;
  }
  .summary-calc:focus-visible {
    outline: var(--theme-outline);
    border-radius: var(--theme-radius);
  }
</style>
